// Shared customer data and types

import type { RiskLevelCode } from '@/lib/risk-level';
import { daysAgo } from '@/lib/demo-data/relative-date';

export interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: 'GBP' | 'EUR' | 'USD' | 'CAD' | 'AUD';
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  risk: RiskLevelCode;
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  paymentTerms: string;
  collectionStrategyId: string;
  statementTemplateId?: string; // Assigned statement template ID (undefined = no template, use company default)
  creditControllerId?: string; // Assigned credit controller user ID
  accountManagerId?: string; // Assigned account manager user ID
  onStop?: boolean; // ERP field - indicates if account is on stop
  status?: 'Active' | 'Inactive'; // ERP field - account status
  userSortKey1?: string; // ERP field - customizable category key 1
  userSortKey2?: string; // ERP field - customizable category key 2
  userSortKey3?: string; // ERP field - customizable category key 3
  userSortKey4?: string; // ERP field - customizable category key 4
  userSortKey1Label?: string; // ERP field label for user sort key 1
  userSortKey2Label?: string; // ERP field label for user sort key 2
  userSortKey3Label?: string; // ERP field label for user sort key 3
  userSortKey4Label?: string; // ERP field label for user sort key 4
  dueDays?: number; // ERP field - number of days until payment is due
  dueDaysType?: 'Days from Invoice' | 'End of Following Month'; // ERP field - how due days should be calculated
  groupDso?: number; // Group-level Days Sales Outstanding (payment performance)
  /** Finance system "Any Currency" flag is set AND account has multi-currency transactions.
   *  When true the displayed currency is set to base (GBP). */
  isAnyCurrency?: boolean;
  /** All distinct currency codes this account has transactions posted in.
   *  Only populated when isAnyCurrency is true. */
  transactionCurrencies?: string[];
}

// Mock Data - 58 Customers
export const mockCustomers: Customer[] = [
  // Critical Risk Customers (2)
  {
    id: '57',
    name: 'Zenith Overdue Holdings',
    accountNumber: 'ACC-057',
    currency: 'GBP',
    outstanding: 480000,
    overdue: 480000,
    creditLimit: 300000,
    dso: 142,
    risk: 'critical',
    lastPayment: '2023-06-20',
    contact: 'frank.nolan@zenithoverdue.co.uk',
    phone: '+44 20 7999 1234',
    address: '1 Delinquent Lane, London, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
    creditControllerId: '1',
    accountManagerId: '3',
    onStop: true,
    dueDays: 30,
    dueDaysType: 'Days from Invoice',
  },
  {
    id: '58',
    name: 'Redline Logistics Ltd',
    accountNumber: 'ACC-058',
    currency: 'EUR',
    outstanding: 320000,
    overdue: 320000,
    creditLimit: 200000,
    dso: 128,
    risk: 'critical',
    lastPayment: '2023-07-14',
    contact: 'elena.voss@redlinelogistics.eu',
    phone: '+49 30 5678 9012',
    address: '42 Freight Strasse, Berlin, Germany',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
    creditControllerId: '2',
    onStop: true,
    dueDays: 30,
    dueDaysType: 'Days from Invoice',
  },
  // High Risk Customers (12) - Majority GBP (8), some EUR (2), USD (1), AUD (1)
  {
    id: '1',
    name: 'Acme Corporation',
    accountNumber: 'ACC-001',
    currency: 'GBP',
    outstanding: 214000,
    overdue: 214000,
    creditLimit: 250000,
    // DSO == weighted-average age of Acme's outstanding invoices (see demo-data/customer-transactions.ts)
    dso: 95,
    risk: 'high',
    lastPayment: daysAgo(20),
    contact: 'john.doe@acme.com',
    phone: '+44 20 7123 4567',
    address: '123 Business St, London, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
    creditControllerId: '1',
    accountManagerId: '2',
    onStop: true,
    dueDays: 30,
    dueDaysType: 'Days from Invoice',
    groupDso: 88, // Group average payment performance
    userSortKey1: 'North',
    userSortKey2: 'Retail',
    userSortKey3: 'Platinum',
    userSortKey4: 'UK',
    userSortKey1Label: 'Region',
    userSortKey2Label: 'Sector',
    userSortKey3Label: 'Tier',
    userSortKey4Label: 'Country',
  },
  {
    id: '2',
    name: 'Global Tech Industries',
    accountNumber: 'ACC-002',
    currency: 'GBP',
    outstanding: 198000,
    overdue: 198000,
    creditLimit: 300000,
    dso: 102,
    risk: 'high',
    lastPayment: '2023-10-20',
    contact: 'sarah.smith@globaltech.co.uk',
    phone: '+44 161 234 5678',
    address: '456 Tech Ave, Manchester, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
    creditControllerId: '3',
    accountManagerId: '1',
    groupDso: 96, // Group average payment performance
    userSortKey1: 'North',
    userSortKey2: 'Technology',
    userSortKey3: 'Gold',
    userSortKey4: 'UK',
    userSortKey1Label: 'Region',
    userSortKey2Label: 'Sector',
    userSortKey3Label: 'Tier',
    userSortKey4Label: 'Country',
  },
  {
    id: '3',
    name: 'Mega Retail Group',
    accountNumber: 'ACC-003',
    currency: 'GBP',
    outstanding: 187000,
    overdue: 187000,
    creditLimit: 200000,
    dso: 88,
    risk: 'high',
    lastPayment: '2023-12-01',
    contact: 'michael.brown@megaretail.co.uk',
    phone: '+44 121 456 7890',
    address: '789 Retail Rd, Birmingham, UK',
    paymentTerms: 'Net 45',
    collectionStrategyId: '3',
    creditControllerId: '2',
    accountManagerId: '5',
    onStop: true,
    dueDays: 45,
    dueDaysType: 'End of Following Month',
    groupDso: 82, // Group average payment performance
    userSortKey1: 'Midlands',
    userSortKey2: 'Retail',
    userSortKey3: 'Silver',
    userSortKey4: 'UK',
    userSortKey1Label: 'Region',
    userSortKey2Label: 'Sector',
    userSortKey3Label: 'Tier',
    userSortKey4Label: 'Country',
  },
  {
    id: '4',
    name: 'Premier Manufacturing',
    accountNumber: 'ACC-004',
    currency: 'GBP',
    outstanding: 176000,
    overdue: 176000,
    creditLimit: 220000,
    dso: 91,
    risk: 'high',
    lastPayment: '2023-11-28',
    contact: 'lisa.johnson@premier.co.uk',
    phone: '+44 113 345 6789',
    address: '321 Factory Ln, Leeds, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '3',
    creditControllerId: '1',
    accountManagerId: '3',
    userSortKey1: 'North',
    userSortKey2: 'Manufacturing',
    userSortKey3: 'Platinum',
    userSortKey4: 'UK',
    userSortKey1Label: 'Region',
    userSortKey2Label: 'Sector',
    userSortKey3Label: 'Tier',
    userSortKey4Label: 'Country',
  },
  {
    id: '5',
    name: 'Sunrise Enterprises',
    accountNumber: 'ACC-005',
    currency: 'GBP',
    outstanding: 165000,
    overdue: 165000,
    creditLimit: 180000,
    dso: 97,
    risk: 'high',
    lastPayment: '2023-11-10',
    contact: 'david.wilson@sunrise.co.uk',
    phone: '+44 20 8123 4567',
    address: '654 Enterprise Way, Manchester, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
    creditControllerId: '5',
    onStop: true,
    groupDso: 91, // Group average payment performance
    userSortKey1: 'South',
    userSortKey2: 'Services',
    userSortKey3: 'Gold',
    userSortKey4: 'UK',
    userSortKey1Label: 'Region',
    userSortKey2Label: 'Sector',
    userSortKey3Label: 'Tier',
    userSortKey4Label: 'Country',
  },
  {
    id: '6',
    name: 'Quantum Solutions',
    accountNumber: 'ACC-006',
    currency: 'GBP',
    outstanding: 154000,
    overdue: 154000,
    creditLimit: 200000,
    dso: 85,
    risk: 'high',
    lastPayment: '2023-12-05',
    contact: 'emily.davis@quantum.co.uk',
    phone: '+44 117 456 7890',
    address: '987 Innovation Dr, Bristol, UK',
    paymentTerms: 'Net 30',
    userSortKey1: 'South',
    userSortKey2: 'Technology',
    userSortKey3: 'Silver',
    userSortKey4: 'UK',
    userSortKey1Label: 'Region',
    userSortKey2Label: 'Sector',
    userSortKey3Label: 'Tier',
    userSortKey4Label: 'Country',
    collectionStrategyId: '3',
  },
  {
    id: '7',
    name: 'Atlantic Trading Co',
    accountNumber: 'ACC-007',
    // Any Currency account: Finance system flag is set and transactions exist in multiple currencies.
    // Default currency is forced to GBP (base).
    currency: 'GBP',
    isAnyCurrency: true,
    transactionCurrencies: ['GBP', 'EUR', 'USD', 'CAD'],
    outstanding: 143000,
    overdue: 143000,
    creditLimit: 160000,
    dso: 93,
    risk: 'high',
    lastPayment: '2023-11-18',
    contact: 'robert.miller@atlantic.eu',
    phone: '+33 1 42 12 34 56',
    address: '234 Commerce Blvd, Paris, France',
    paymentTerms: 'Net 45',
    collectionStrategyId: '',
    groupDso: 87, // Group average payment performance
  },
  {
    id: '8',
    name: 'Pacific Imports',
    accountNumber: 'ACC-008',
    currency: 'AUD',
    outstanding: 132000,
    overdue: 132000,
    creditLimit: 150000,
    dso: 89,
    risk: 'high',
    lastPayment: '2023-11-25',
    contact: 'jennifer.taylor@pacific.au',
    phone: '+61 2 9123 4567',
    address: '567 Harbor St, Sydney, Australia',
    paymentTerms: 'Net 30',
    collectionStrategyId: '3',
  },
  {
    id: '9',
    name: 'Northern Logistics',
    accountNumber: 'ACC-009',
    currency: 'GBP',
    outstanding: 121000,
    overdue: 121000,
    creditLimit: 140000,
    dso: 86,
    risk: 'high',
    lastPayment: '2023-12-02',
    contact: 'william.anderson@northern.co.uk',
    phone: '+44 141 234 5678',
    address: '890 Transport Ave, Glasgow, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
  },
  {
    id: '10',
    name: 'Summit Distribution',
    accountNumber: 'ACC-010',
    currency: 'GBP',
    outstanding: 110000,
    overdue: 110000,
    creditLimit: 130000,
    dso: 92,
    risk: 'high',
    lastPayment: '2023-11-22',
    contact: 'mary.thomas@summit.co.uk',
    phone: '+44 131 567 8901',
    address: '123 Distribution Way, Edinburgh, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '11',
    name: 'Coastal Ventures',
    accountNumber: 'ACC-011',
    currency: 'GBP',
    outstanding: 99000,
    overdue: 99000,
    creditLimit: 120000,
    dso: 84,
    risk: 'high',
    lastPayment: '2023-12-08',
    contact: 'james.jackson@coastal.co.uk',
    phone: '+44 20 9123 4567',
    address: '456 Seaside Rd, Brighton, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '3',
  },
  {
    id: '12',
    name: 'Metro Services Group',
    accountNumber: 'ACC-012',
    // Any Currency account: Finance system flag is set and transactions exist in multiple currencies.
    // Default currency is forced to GBP (base).
    currency: 'GBP',
    isAnyCurrency: true,
    transactionCurrencies: ['GBP', 'USD', 'EUR'],
    outstanding: 88000,
    overdue: 88000,
    creditLimit: 110000,
    dso: 87,
    risk: 'high',
    lastPayment: '2023-11-30',
    contact: 'patricia.white@metro.com',
    phone: '+44 117 929 0306',
    address: '789 Metro Plaza, Chicago, IL 60601',
    paymentTerms: 'Net 30',
    collectionStrategyId: '4',
  },
  // Medium Risk Customers (20) - Majority GBP (14), some EUR (3), USD (2), CAD (1)
  {
    id: '13',
    name: 'Horizon Technologies',
    accountNumber: 'ACC-013',
    currency: 'GBP',
    outstanding: 145000,
    overdue: 72500,
    creditLimit: 200000,
    dso: 65,
    risk: 'medium',
    lastPayment: '2024-01-10',
    contact: 'christopher.harris@horizon.co.uk',
    phone: '+44 20 7890 1234',
    address: '321 Tech Park, Cambridge, UK',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
    groupDso: 60, // Group average payment performance
  },
  {
    id: '14',
    name: 'Silverline Industries',
    accountNumber: 'ACC-014',
    currency: 'GBP',
    outstanding: 138000,
    overdue: 69000,
    creditLimit: 180000,
    dso: 62,
    risk: 'medium',
    lastPayment: '2024-01-15',
    contact: 'barbara.martin@silverline.co.uk',
    phone: '+44 114 234 5678',
    address: '654 Industrial St, Sheffield, UK',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '15',
    name: 'Apex Commercial',
    accountNumber: 'ACC-015',
    currency: 'GBP',
    outstanding: 127000,
    overdue: 63500,
    creditLimit: 170000,
    dso: 58,
    risk: 'medium',
    lastPayment: '2024-01-20',
    contact: 'daniel.thompson@apex.co.uk',
    phone: '+44 151 890 1234',
    address: '987 Commerce Dr, Liverpool, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '16',
    name: 'Pinnacle Systems',
    accountNumber: 'ACC-016',
    currency: 'GBP',
    outstanding: 116000,
    overdue: 58000,
    creditLimit: 150000,
    dso: 55,
    risk: 'medium',
    lastPayment: '2024-01-25',
    contact: 'nancy.garcia@pinnacle.co.uk',
    phone: '+44 20 1234 5678',
    address: '234 Systems Ave, Birmingham, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '17',
    name: 'Vertex Corporation',
    accountNumber: 'ACC-017',
    currency: 'GBP',
    outstanding: 105000,
    overdue: 52500,
    creditLimit: 140000,
    dso: 52,
    risk: 'medium',
    lastPayment: '2024-02-01',
    contact: 'matthew.martinez@vertex.co.uk',
    phone: '+44 29 2090 1234',
    address: '567 Corporate Blvd, Cardiff, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '5',
  },
  {
    id: '18',
    name: 'Fusion Enterprises',
    accountNumber: 'ACC-018',
    currency: 'CAD',
    outstanding: 94000,
    overdue: 47000,
    creditLimit: 130000,
    dso: 49,
    risk: 'medium',
    lastPayment: '2024-02-05',
    contact: 'karen.robinson@fusion.ca',
    phone: '+1 (604) 123-4567',
    address: '890 Enterprise Way, Vancouver, Canada',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '19',
    name: 'Nexus Trading',
    accountNumber: 'ACC-019',
    // Any Currency account: Finance system flag is set and transactions exist in multiple currencies.
    // Default currency is forced to GBP (base).
    currency: 'GBP',
    isAnyCurrency: true,
    transactionCurrencies: ['GBP', 'EUR', 'AUD'],
    outstanding: 83000,
    overdue: 41500,
    creditLimit: 120000,
    dso: 46,
    risk: 'medium',
    lastPayment: '2024-02-10',
    contact: 'steven.clark@nexus.eu',
    phone: '+31 20 123 4567',
    address: '123 Trade Center, Amsterdam, Netherlands',
    paymentTerms: 'Net 45',
    collectionStrategyId: '',
  },
  {
    id: '20',
    name: 'Catalyst Solutions',
    accountNumber: 'ACC-020',
    currency: 'GBP',
    outstanding: 72000,
    overdue: 36000,
    creditLimit: 110000,
    dso: 43,
    risk: 'medium',
    lastPayment: '2024-02-15',
    contact: 'betty.rodriguez@catalyst.co.uk',
    phone: '+44 118 901 2345',
    address: '456 Solution St, Reading, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '21',
    name: 'Zenith Group',
    accountNumber: 'ACC-021',
    currency: 'GBP',
    outstanding: 61000,
    overdue: 30500,
    creditLimit: 100000,
    dso: 40,
    risk: 'medium',
    lastPayment: '2024-02-20',
    contact: 'donald.lewis@zenith.co.uk',
    phone: '+44 1603 912 3456',
    address: '789 Business Park, Norwich, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '5',
  },
  {
    id: '22',
    name: 'Vanguard Industries',
    accountNumber: 'ACC-022',
    currency: 'GBP',
    outstanding: 125000,
    overdue: 62500,
    creditLimit: 160000,
    dso: 67,
    risk: 'medium',
    lastPayment: '2024-01-12',
    contact: 'dorothy.lee@vanguard.co.uk',
    phone: '+44 1382 123 4567',
    address: '321 Industrial Pkwy, Dundee, UK',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '23',
    name: 'Omega Systems',
    accountNumber: 'ACC-023',
    currency: 'GBP',
    outstanding: 114000,
    overdue: 57000,
    creditLimit: 150000,
    dso: 64,
    risk: 'medium',
    lastPayment: '2024-01-18',
    contact: 'joshua.walker@omega.co.uk',
    phone: '+44 20 2345 6789',
    address: '654 Tech Quarter, Leeds, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '24',
    name: 'Titan Commercial',
    accountNumber: 'ACC-024',
    currency: 'EUR',
    outstanding: 103000,
    overdue: 51500,
    creditLimit: 140000,
    dso: 61,
    risk: 'medium',
    lastPayment: '2024-01-22',
    contact: 'sandra.hall@titan.eu',
    phone: '+34 91 123 4567',
    address: '987 Commercial Ave, Madrid, Spain',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '25',
    name: 'Eclipse Trading',
    accountNumber: 'ACC-025',
    currency: 'GBP',
    outstanding: 92000,
    overdue: 46000,
    creditLimit: 130000,
    dso: 58,
    risk: 'medium',
    lastPayment: '2024-01-28',
    contact: 'ashley.allen@eclipse.co.uk',
    phone: '+44 1752 234 5678',
    address: '234 Trade Plaza, Plymouth, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '5',
  },
  {
    id: '26',
    name: 'Meridian Group',
    accountNumber: 'ACC-026',
    currency: 'GBP',
    outstanding: 81000,
    overdue: 40500,
    creditLimit: 120000,
    dso: 55,
    risk: 'medium',
    lastPayment: '2024-02-03',
    contact: 'kimberly.young@meridian.co.uk',
    phone: '+44 1224 123 4567',
    address: '567 Group Center, Aberdeen, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '27',
    name: 'Spectrum Solutions',
    accountNumber: 'ACC-027',
    currency: 'GBP',
    outstanding: 70000,
    overdue: 35000,
    creditLimit: 110000,
    dso: 52,
    risk: 'medium',
    lastPayment: '2024-02-08',
    contact: 'mark.hernandez@spectrum.co.uk',
    phone: '+44 1865 345 6789',
    address: '890 Solution Way, Oxford, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '28',
    name: 'Prism Enterprises',
    accountNumber: 'ACC-028',
    currency: 'GBP',
    outstanding: 118000,
    overdue: 59000,
    creditLimit: 155000,
    dso: 69,
    risk: 'medium',
    lastPayment: '2024-01-08',
    contact: 'donna.king@prism.co.uk',
    phone: '+44 20 3456 7890',
    address: '123 Enterprise Rd, Glasgow, UK',
    paymentTerms: 'Net 45',
    collectionStrategyId: '1',
  },
  {
    id: '29',
    name: 'Cascade Industries',
    accountNumber: 'ACC-029',
    currency: 'EUR',
    outstanding: 107000,
    overdue: 53500,
    creditLimit: 145000,
    dso: 66,
    risk: 'medium',
    lastPayment: '2024-01-14',
    contact: 'paul.wright@cascade.eu',
    phone: '+41 22 123 4567',
    address: '456 Industrial Zone, Geneva, Switzerland',
    paymentTerms: 'Net 45',
    collectionStrategyId: '5',
  },
  {
    id: '30',
    name: 'Stellar Corporation',
    accountNumber: 'ACC-030',
    currency: 'GBP',
    outstanding: 96000,
    overdue: 48000,
    creditLimit: 135000,
    dso: 63,
    risk: 'medium',
    lastPayment: '2024-01-19',
    contact: 'carol.lopez@stellar.co.uk',
    phone: '+44 1273 456 7890',
    address: '789 Corporate Center, Brighton, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '31',
    name: 'Quantum Dynamics',
    accountNumber: 'ACC-031',
    currency: 'GBP',
    outstanding: 85000,
    overdue: 42500,
    creditLimit: 125000,
    dso: 60,
    risk: 'medium',
    lastPayment: '2024-01-24',
    contact: 'george.hill@quantum.co.uk',
    phone: '+44 1223 912 3456',
    address: '321 Dynamics Dr, Cambridge, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '32',
    name: 'Nexus Ventures',
    accountNumber: 'ACC-032',
    currency: 'USD',
    outstanding: 74000,
    overdue: 37000,
    creditLimit: 115000,
    dso: 57,
    risk: 'medium',
    lastPayment: '2024-01-30',
    contact: 'ruth.scott@nexusv.com',
    phone: '+44 113 496 0305',
    address: '654 Venture Blvd, San Diego, CA 92101',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  // Low Risk Customers (24) - Majority GBP (18), some EUR (3), USD (2), CAD (1)
  {
    id: '33',
    name: 'Bright Future Inc',
    accountNumber: 'ACC-033',
    currency: 'GBP',
    outstanding: 89000,
    overdue: 0,
    creditLimit: 150000,
    dso: 28,
    risk: 'low',
    lastPayment: '2024-03-01',
    contact: 'frank.green@brightfuture.co.uk',
    phone: '+44 1908 678 9012',
    address: '987 Future Ave, Milton Keynes, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '34',
    name: 'Swift Solutions',
    accountNumber: 'ACC-034',
    currency: 'GBP',
    outstanding: 78000,
    overdue: 0,
    creditLimit: 130000,
    dso: 25,
    risk: 'low',
    lastPayment: '2024-03-05',
    contact: 'virginia.adams@swift.co.uk',
    phone: '+44 1793 123 4567',
    address: '234 Solution St, Swindon, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '35',
    name: 'Reliable Trading Co',
    accountNumber: 'ACC-035',
    currency: 'GBP',
    outstanding: 67000,
    overdue: 0,
    creditLimit: 120000,
    dso: 22,
    risk: 'low',
    lastPayment: '2024-03-08',
    contact: 'henry.baker@reliable.co.uk',
    phone: '+44 20 4567 8901',
    address: '567 Trade Center, Edinburgh, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '36',
    name: 'Premier Partners',
    accountNumber: 'ACC-036',
    currency: 'GBP',
    outstanding: 56000,
    overdue: 0,
    creditLimit: 100000,
    dso: 19,
    risk: 'low',
    lastPayment: '2024-03-10',
    contact: 'frances.gonzalez@premier.co.uk',
    phone: '+44 1603 789 0123',
    address: '890 Partner Plaza, Norwich, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '37',
    name: 'Excellence Corp',
    accountNumber: 'ACC-037',
    currency: 'CAD',
    outstanding: 95000,
    overdue: 4750,
    creditLimit: 140000,
    dso: 32,
    risk: 'low',
    lastPayment: '2024-02-28',
    contact: 'roy.nelson@excellence.ca',
    phone: '+1 (514) 123-4567',
    address: '123 Excellence Way, Montreal, Canada',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '38',
    name: 'Integrity Systems',
    accountNumber: 'ACC-038',
    currency: 'GBP',
    outstanding: 84000,
    overdue: 4200,
    creditLimit: 130000,
    dso: 29,
    risk: 'low',
    lastPayment: '2024-03-02',
    contact: 'irene.carter@integrity.co.uk',
    phone: '+44 1234 890 1234',
    address: '456 Integrity Dr, Northampton, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '39',
    name: 'Quality Enterprises',
    accountNumber: 'ACC-039',
    currency: 'GBP',
    outstanding: 73000,
    overdue: 3650,
    creditLimit: 120000,
    dso: 26,
    risk: 'low',
    lastPayment: '2024-03-04',
    contact: 'randy.mitchell@quality.co.uk',
    phone: '+44 1582 123 4567',
    address: '789 Quality Blvd, Luton, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '40',
    name: 'Trustworthy Trading',
    accountNumber: 'ACC-040',
    currency: 'GBP',
    outstanding: 62000,
    overdue: 3100,
    creditLimit: 110000,
    dso: 23,
    risk: 'low',
    lastPayment: '2024-03-06',
    contact: 'marilyn.perez@trustworthy.co.uk',
    phone: '+44 20 5678 9012',
    address: '321 Trust Ave, Cardiff, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '41',
    name: 'Precision Industries',
    accountNumber: 'ACC-041',
    currency: 'GBP',
    outstanding: 91000,
    overdue: 0,
    creditLimit: 135000,
    dso: 30,
    risk: 'low',
    lastPayment: '2024-03-01',
    contact: 'arthur.roberts@precision.co.uk',
    phone: '+44 1772 901 2345',
    address: '654 Precision Way, Preston, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '42',
    name: 'Efficient Solutions',
    accountNumber: 'ACC-042',
    currency: 'GBP',
    outstanding: 80000,
    overdue: 0,
    creditLimit: 125000,
    dso: 27,
    risk: 'low',
    lastPayment: '2024-03-03',
    contact: 'evelyn.turner@efficient.co.uk',
    phone: '+44 1202 912 3456',
    address: '987 Efficiency Dr, Bournemouth, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '43',
    name: 'Dependable Corp',
    accountNumber: 'ACC-043',
    currency: 'GBP',
    outstanding: 69000,
    overdue: 0,
    creditLimit: 115000,
    dso: 24,
    risk: 'low',
    lastPayment: '2024-03-07',
    contact: 'harold.phillips@dependable.co.uk',
    phone: '+44 1752 012 3456',
    address: '234 Dependable St, Plymouth, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '44',
    name: 'Solid Ventures',
    accountNumber: 'ACC-044',
    currency: 'GBP',
    outstanding: 58000,
    overdue: 0,
    creditLimit: 105000,
    dso: 21,
    risk: 'low',
    lastPayment: '2024-03-09',
    contact: 'mildred.campbell@solid.co.uk',
    phone: '+44 1482 123 4567',
    address: '567 Venture Park, Hull, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '45',
    name: 'Stable Systems',
    accountNumber: 'ACC-045',
    currency: 'EUR',
    outstanding: 87000,
    overdue: 4350,
    creditLimit: 130000,
    dso: 31,
    risk: 'low',
    lastPayment: '2024-02-29',
    contact: 'clarence.parker@stable.eu',
    phone: '+45 33 12 34 56',
    address: '890 Stable Way, Copenhagen, Denmark',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '46',
    name: 'Consistent Trading',
    accountNumber: 'ACC-046',
    currency: 'GBP',
    outstanding: 76000,
    overdue: 3800,
    creditLimit: 120000,
    dso: 28,
    risk: 'low',
    lastPayment: '2024-03-02',
    contact: 'carl.evans@consistent.co.uk',
    phone: '+44 20 6789 0123',
    address: '123 Consistent Rd, Liverpool, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '47',
    name: 'Steady Enterprises',
    accountNumber: 'ACC-047',
    currency: 'GBP',
    outstanding: 65000,
    overdue: 3250,
    creditLimit: 110000,
    dso: 25,
    risk: 'low',
    lastPayment: '2024-03-05',
    contact: 'willie.edwards@steady.co.uk',
    phone: '+44 1904 123 4567',
    address: '456 Steady Ave, York, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '48',
    name: 'Secure Solutions',
    accountNumber: 'ACC-048',
    currency: 'GBP',
    outstanding: 54000,
    overdue: 0,
    creditLimit: 100000,
    dso: 18,
    risk: 'low',
    lastPayment: '2024-03-11',
    contact: 'lois.collins@secure.co.uk',
    phone: '+44 1225 234 5678',
    address: '789 Secure Blvd, Bath, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '49',
    name: 'Prompt Partners',
    accountNumber: 'ACC-049',
    currency: 'GBP',
    outstanding: 93000,
    overdue: 0,
    creditLimit: 135000,
    dso: 33,
    risk: 'low',
    lastPayment: '2024-02-27',
    contact: 'earl.stewart@prompt.co.uk',
    phone: '+44 1392 123 4567',
    address: '321 Prompt Plaza, Exeter, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '50',
    name: 'Swift Industries',
    accountNumber: 'ACC-050',
    currency: 'GBP',
    outstanding: 82000,
    overdue: 0,
    creditLimit: 125000,
    dso: 30,
    risk: 'low',
    lastPayment: '2024-03-01',
    contact: 'judith.sanchez@swifti.co.uk',
    phone: '+44 1522 812 3456',
    address: '654 Swift Way, Lincoln, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '51',
    name: 'Rapid Response Co',
    accountNumber: 'ACC-051',
    currency: 'GBP',
    outstanding: 71000,
    overdue: 0,
    creditLimit: 115000,
    dso: 27,
    risk: 'low',
    lastPayment: '2024-03-04',
    contact: 'frederick.morris@rapid.co.uk',
    phone: '+44 1206 345 6789',
    address: '987 Response Rd, Colchester, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
  {
    id: '52',
    name: 'Timely Trading',
    accountNumber: 'ACC-052',
    currency: 'GBP',
    outstanding: 60000,
    overdue: 0,
    creditLimit: 105000,
    dso: 24,
    risk: 'low',
    lastPayment: '2024-03-07',
    contact: 'joyce.rogers@timely.co.uk',
    phone: '+44 1473 123 4567',
    address: '234 Timely Center, Ipswich, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '53',
    name: 'Punctual Systems',
    accountNumber: 'ACC-053',
    currency: 'GBP',
    outstanding: 88000,
    overdue: 4400,
    creditLimit: 130000,
    dso: 34,
    risk: 'low',
    lastPayment: '2024-02-26',
    contact: 'gerald.reed@punctual.co.uk',
    phone: '+44 20 7890 1234',
    address: '567 Punctual Ave, Bristol, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '1',
  },
  {
    id: '54',
    name: 'Expedite Enterprises',
    accountNumber: 'ACC-054',
    currency: 'EUR',
    outstanding: 77000,
    overdue: 3850,
    creditLimit: 120000,
    dso: 31,
    risk: 'low',
    lastPayment: '2024-02-29',
    contact: 'alice.cook@expedite.eu',
    phone: '+47 22 12 34 56',
    address: '890 Expedite Way, Oslo, Norway',
    paymentTerms: 'Net 30',
    collectionStrategyId: '',
  },
  {
    id: '55',
    name: 'Agile Corporation',
    accountNumber: 'ACC-055',
    currency: 'GBP',
    outstanding: 66000,
    overdue: 3300,
    creditLimit: 110000,
    dso: 28,
    risk: 'low',
    lastPayment: '2024-03-03',
    contact: 'teresa.morgan@agile.co.uk',
    phone: '+44 1962 456 7890',
    address: '123 Agile Blvd, Winchester, UK',
    paymentTerms: 'Net 30',
    collectionStrategyId: '5',
  },
  {
    id: '56',
    name: 'Dynamic Solutions',
    accountNumber: 'ACC-056',
    currency: 'USD',
    outstanding: 55000,
    overdue: 0,
    creditLimit: 100000,
    dso: 20,
    risk: 'low',
    lastPayment: '2024-03-10',
    contact: 'joe.bell@dynamic.com',
    phone: '+44 113 496 0305',
    address: '456 Dynamic Dr, Kansas City, MO 64101',
    paymentTerms: 'Net 30',
    collectionStrategyId: '2',
  },
];

// Helper function to get customer by ID
export function getCustomerById(id: string): Customer | null {
  return mockCustomers.find((c) => c.id === id) || null;
}
